import { FormBuilder } from "@components/ui/FormBuilder";
import type { FieldConfig } from "@components/ui/FormBuilder";
import { createField } from "@components/ui/formFieldHelpers";
import {
  colorRenderer,
  selectFontRenderer,
  selectIntermedioRender,
} from "@components/ui/fieldRederers";
import { z } from "zod";
import type {
  ClienteFinal,
  UpdateClienteFinalPayload,
} from "@interfaces/user.interface";
import { useUpdateClienteFinalWithUsuario } from "@hooks/clientes-finales/useUpdateClienteFinalWithUsuario";
import { subirLogoClienteFinal } from "@/lib/logoClienteFinal";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";

const schema = z.object({
  usuario: z.object({
    nombreUsuario: z.string().min(2).optional(),
    nombre: z.string().min(1).optional(),
    apellidoPaterno: z.string().min(1).optional(),
    apellidoMaterno: z.string().optional(),
    correo: z.string().email().optional(),
  }),
  cliente: z.object({
    nombreEmpresa: z.string().min(1).optional(),
    colorPrimario: z.string().optional(),
    colorSecundario: z.string().optional(),
    colorTextoPrimario: z.string().optional(),
    tipografiaMarca: z.string().optional(),
    logoMarca: z
      .object({
        llave: z.string().optional(),
        nombre: z.string().optional(),
        url: z.string().url().optional(),
      })
      .nullable()
      .optional(),
    logoMarcaInverso: z
      .object({
        llave: z.string().optional(),
        nombre: z.string().optional(),
        url: z.string().url().optional(),
      })
      .nullable()
      .optional(),
    idUsuarioAlta: z.string(),
  }),
  fileBag: z.object({}).optional(),
});

export function EditClienteFinalForm({
  data,
  close,
  queryKey,
}: {
  data: ClienteFinal;
  close: () => void;
  queryKey?: ReadonlyArray<unknown>;
}) {
  const cliente = data;
  const { user } = useCurrentUser();
  const isSuperAdmin = user?.rol.nombre === "SUPER_ADMIN";

  // const selectedFont = form.watch("cliente.tipografiaMarca") || "sans";

  const effectiveQueryKey = queryKey ?? ["clientesFinales"];
  const { mutate } = useUpdateClienteFinalWithUsuario({
    queryKey: effectiveQueryKey,
  });

  type FormData = z.infer<typeof schema>;

  const defaultValues: FormData = {
    usuario: {
      nombreUsuario: cliente.usuario.nombreUsuario,
      nombre: cliente.usuario.nombre,
      apellidoPaterno: cliente.usuario.apellidoPaterno,
      apellidoMaterno: cliente.usuario.apellidoMaterno ?? "",
      correo: cliente.usuario.correo,
    },
    cliente: {
      nombreEmpresa: cliente.nombreEmpresa,
      colorPrimario: cliente.colorPrimario ?? "",
      colorSecundario: cliente.colorSecundario ?? "",
      colorTextoPrimario: cliente.colorTextoPrimario ?? "",
      // colorTerciario1: cliente.colorTerciario1 ?? "",
      // colorTerciario2: cliente.colorTerciario2 ?? "",
      tipografiaMarca: cliente.tipografiaMarca ?? "",
      logoMarca: cliente.logoMarca ?? null,
      logoMarcaInverso: cliente.logoMarcaInverso ?? null,
      idUsuarioAlta: isSuperAdmin ? "" : cliente.idUsuarioAlta.toString() || "",
    },
  };

  const fields: FieldConfig<FormData>[] = [
    createField<FormData>("usuario.nombreUsuario", "text", {
      label: "Nombre de usuario",
      colSpan: "1",
      required: false,
      disabled: false,
      placeholder: " Nombre de usuario",
    }),
    createField<FormData>("usuario.nombre", "text", {
      label: "Nombre",
      colSpan: "1",
      required: false,
      disabled: false,
      placeholder: " Nombre",
    }),
    createField<FormData>("usuario.apellidoPaterno", "text", {
      label: "Apellido paterno",
      colSpan: "1",
      required: false,
      disabled: false,
      placeholder: " Apellido paterno",
    }),
    createField<FormData>("usuario.apellidoMaterno", "text", {
      label: "Apellido materno",
      colSpan: "1",
      required: false,
      disabled: false,
      placeholder: " Apellido materno",
    }),
    createField<FormData>("usuario.correo", "text", {
      label: "Correo",
      colSpan: "1",
      required: false,
      disabled: false,
      placeholder: " correo@ejemplo.com",
    }),
    createField<FormData>("cliente.nombreEmpresa", "text", {
      label: "Nombre de la empresa",
      colSpan: "2",
      required: false,
      disabled: false,
      placeholder: " Nombre de la empresa",
    }),
    // -- Customs
    createField<FormData, "fileImage">("cliente.logoMarca", "fileImage", {
      label: "Logo (solo imagen png, jpg, jpeg, webp, svg)",
      description: "Se usa sobre fondos claros.",
      colSpan: "1",
      required: false,
    }),
    createField<FormData, "fileImage">("cliente.logoMarcaInverso", "fileImage", {
      label: "Logo inverso (opcional)",
      description:
        "Se usa en el modo obscuro del aprendiz. Si no lo subes, se usa el logo normal.",
      colSpan: "1",
      required: false,
      previewBackground: "dark",
      clearable: true,
    }),
    createField<FormData, "color">("cliente.colorPrimario", "color", {
      label: "Color Primario",
      colSpan: "1",
      required: false,
      disabled: false,
    }),
    createField<FormData, "color">("cliente.colorSecundario", "color", {
      label: "Color Secundario",
      colSpan: "1",
      required: false,
      disabled: false,
    }),
    createField<FormData, "color">("cliente.colorTextoPrimario", "color", {
      label: "Color de Texto Primario",
      colSpan: "1",
      required: false,
      disabled: false,
    }),
    // createField<FormData, "color">("cliente.colorTerciario1", "color", {
    //   label: "Color Terciario 1",
    //   colSpan: "1",
    //   required: true,
    //   disabled: false,
    // }),
    // createField<FormData, "color">("cliente.colorTerciario2", "color", {
    //   label: "Color Terciario 2",
    //   colSpan: "1",
    //   required: true,
    //   disabled: false,
    // }),
    createField<FormData, "selectFont">(
      "cliente.tipografiaMarca",
      "selectFont",
      {
        label: "Tipografía",
        colSpan: "2",
        required: false,
        disabled: false,
      },
    ),
    ...(isSuperAdmin
      ? [
          createField<FormData, "selectIntermediario">(
            "cliente.idUsuarioAlta",
            "selectIntermediario",
            {
              label: "Intermediario del cliente final",
              colSpan: "2",
              required: true,
              disabled: false,
            },
          ),
        ]
      : []),
    // -- Customs
  ];

  const handleSubmit = async (
    data: FormData,
    helpers: { fileBag: Record<string, File | null> },
  ) => {
    const logoFile = helpers.fileBag["cliente.logoMarca"] ?? null;
    const logoInversoFile = helpers.fileBag["cliente.logoMarcaInverso"] ?? null;
    const [logoMarca, logoInversoSubido] = await Promise.all([
      logoFile ? subirLogoClienteFinal(logoFile) : undefined,
      logoInversoFile ? subirLogoClienteFinal(logoInversoFile) : undefined,
    ]);
    // Sin archivo nuevo: null si el admin quitó el logo inverso, undefined para conservarlo.
    const logoMarcaInverso =
      logoInversoSubido ??
      (data.cliente.logoMarcaInverso === null && cliente.logoMarcaInverso
        ? null
        : undefined);

    const { fileBag: _fileBag, ...formValues } = data;

    const payload: UpdateClienteFinalPayload = {
      id: typeof cliente.id === "number" ? cliente.id : Number(cliente.id),
      data: {
        usuario: {
          nombreUsuario: formValues.usuario.nombreUsuario ?? "",
          nombre: formValues.usuario.nombre ?? "",
          apellidoPaterno: formValues.usuario.apellidoPaterno ?? "",
          apellidoMaterno: formValues.usuario.apellidoMaterno,
          correo: formValues.usuario.correo ?? "",
        },
        cliente: {
          nombreEmpresa: formValues.cliente.nombreEmpresa ?? "",
          idUsuarioAlta: formValues.cliente.idUsuarioAlta,
          colorPrimario: formValues.cliente.colorPrimario,
          colorSecundario: formValues.cliente.colorSecundario,
          colorTextoPrimario: formValues.cliente.colorTextoPrimario,
          tipografiaMarca: formValues.cliente.tipografiaMarca,
          logoMarca,
          logoMarcaInverso,
        },
      },
    };

    mutate(
      payload,
      {
        onSuccess: () => {
          close();
        },
      },
    );
  };

  return (
    <FormBuilder<FormData>
      schema={schema}
      defaultValues={defaultValues}
      fields={fields}
      onSubmit={handleSubmit}
      submitLabel="Actualizar Cliente"
      registry={{
        color: colorRenderer<FormData>(),
        selectFont: selectFontRenderer<FormData>(),
        ...(isSuperAdmin
          ? {
              selectIntermediario: selectIntermedioRender<FormData>(),
            }
          : {}),
      }}
    />
  );
}

// import { useForm } from "react-hook-form";
// import { useState } from "react";
// import { zodResolver } from "@hookform/resolvers/zod";
// import { z } from "zod";
// import { Input } from "@components/ui/input";
// import { Button } from "@components/ui/button";
// import defaultLogo from "@/assets/default-logo.png";
// import {
//   Form,
//   FormControl,
//   FormField,
//   FormItem,
//   FormLabel,
//   FormMessage,
// } from "@components/ui/form";
// import { ColorPicker } from "@components/ui/colorPicker";
// import { useUpdateClienteFinalWithUsuario } from "@hooks/clientes-finales/useUpdateClienteFinalWithUsuario";
// import type { ClienteFinal } from "@interfaces/user.interface";
// import { SelectTipografia } from "@components/ui/SelectTipografia";
// import { uploadPresignedFile } from "@retro-ia/shared/upload";

// const schema = z.object({
//   usuario: z.object({
//     nombreUsuario: z.string().min(2),
//     nombre: z.string().min(1),
//     apellidoPaterno: z.string().min(1),
//     apellidoMaterno: z.string().optional(),
//     correo: z.string().email(),
//   }),
//   cliente: z.object({
//     nombreEmpresa: z.string().min(1),
//     colorPrimarioMarca: z.string().optional(),
//     colorSecundarioMarca: z.string().optional(),
//     colorTerciarioMarca: z.string().optional(),
//     colorAcentoMarca: z.string().optional(),
//     tipografiaMarca: z.string().optional(),
//     logoMarca: z
//       .object({
//         llave: z.string(),
//         nombre: z.string(),
//         url: z.string().url().optional(),
//       })
//       .nullable()
//       .optional(),
//   }),
// });

// type FormData = z.infer<typeof schema>;

// export function EditClienteFinalForm({
//   data,
//   close,
// }: {
//   data: ClienteFinal;
//   close: () => void;
// }) {
//   const cliente = data;
//   const [logoFile, setLogoFile] = useState<File | null>(null);
//   const form = useForm<FormData>({
//     resolver: zodResolver(schema),
//     defaultValues: {
//       usuario: {
//         nombreUsuario: cliente.usuario.nombreUsuario,
//         nombre: cliente.usuario.nombre,
//         apellidoPaterno: cliente.usuario.apellidoPaterno,
//         apellidoMaterno: cliente.usuario.apellidoMaterno ?? "",
//         correo: cliente.usuario.correo,
//       },
//       cliente: {
//         nombreEmpresa: cliente.nombreEmpresa,
//         colorPrimarioMarca: cliente.colorPrimarioMarca ?? "",
//         colorSecundarioMarca: cliente.colorSecundarioMarca ?? "",
//         colorTerciarioMarca: cliente.colorTerciarioMarca ?? "",
//         colorAcentoMarca: cliente.colorAcentoMarca ?? "",
//         tipografiaMarca: cliente.tipografiaMarca ?? "",
//         // logoMarca: cliente.logoMarca ?? null,
//       },
//     },
//   });

//   const selectedFont = form.watch("cliente.tipografiaMarca") || "sans";

//   const { mutate, isPending } = useUpdateClienteFinalWithUsuario({
//     queryKey: ["misClientesFinales"],
//   });

//   const onSubmit = async (data: FormData) => {
//     let logoMarca = cliente.logoMarca;
//     if (logoFile) {
//       // Subir logo como público usando el utilitario
//       const res = await uploadPresignedFile({
//         file: logoFile,
//         ruta: "public/img/clientesFinales/" + logoFile.name,
//         fileName: logoFile.name,
//         fileType: logoFile.type,
//         isPublic: true,
//       });
//       logoMarca = res;
//     }

//     mutate(
//       {
//         id: typeof cliente.id === "number" ? cliente.id : Number(cliente.id),
//         data: {
//           ...data,
//           cliente: {
//             ...data.cliente,
//             logoMarca,
//           },
//         },
//       },
//       {
//         onSuccess: () => {
//           form.reset();
//           close();
//         },
//       }
//     );
//   };

//   return (
//     <>
//       <div className="flex justify-center items-center px-4">
//         <Form {...form}>
//           <form
//             onSubmit={form.handleSubmit(onSubmit)}
//             className="space-y-6 w-full"
//           >
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               <FormField
//                 control={form.control}
//                 name="usuario.nombreUsuario"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel>Nombre de usuario</FormLabel>
//                     <FormControl>
//                       <Input placeholder="Nombre de usuario" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="usuario.nombre"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel>Nombre</FormLabel>
//                     <FormControl>
//                       <Input placeholder="Nombre" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="usuario.apellidoPaterno"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel>Apellido paterno</FormLabel>
//                     <FormControl>
//                       <Input placeholder="Apellido paterno" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="usuario.apellidoMaterno"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel>Apellido materno</FormLabel>
//                     <FormControl>
//                       <Input placeholder="Apellido materno" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="usuario.correo"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel>Correo</FormLabel>
//                     <FormControl>
//                       <Input placeholder="correo@ejemplo.com" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />

//               <FormField
//                 control={form.control}
//                 name="cliente.nombreEmpresa"
//                 render={({ field }) => (
//                   <FormItem className="md:col-span-2">
//                     <FormLabel>Nombre de la empresa</FormLabel>
//                     <FormControl>
//                       <Input placeholder="Nombre de la empresa" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />

//               <FormField
//                 control={form.control}
//                 name="cliente.logoMarca"
//                 render={() => (
//                   <FormItem className="md:col-span-2">
//                     <FormLabel>
//                       Logo (solo imagen png, jpg, jpeg, webp)
//                     </FormLabel>
//                     <FormControl>
//                       <div className="flex flex-col gap-2">
//                         <label
//                           htmlFor="logo-upload"
//                           className="cursor-pointer px-4 py-2 rounded bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200 transition flex items-center gap-2"
//                         >
//                           <svg
//                             className="w-5 h-5 text-gray-500"
//                             fill="none"
//                             stroke="currentColor"
//                             strokeWidth={2}
//                             viewBox="0 0 24 24"
//                           >
//                             <path
//                               strokeLinecap="round"
//                               strokeLinejoin="round"
//                               d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12"
//                             />
//                           </svg>
//                           <span>
//                             {logoFile
//                               ? "Cambiar imagen"
//                               : "Selecciona una imagen"}
//                           </span>
//                         </label>
//                         <input
//                           id="logo-upload"
//                           type="file"
//                           accept="image/png,image/jpeg,image/jpg,image/webp"
//                           className="hidden"
//                           onChange={(e) => {
//                             const file = e.target.files?.[0] ?? null;
//                             setLogoFile(file);
//                           }}
//                         />
//                       </div>
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />

//               {(
//                 [
//                   "colorPrimarioMarca",
//                   "colorSecundarioMarca",
//                   "colorTerciarioMarca",
//                   "colorAcentoMarca",
//                 ] as const
//               ).map((campo) => (
//                 <FormField
//                   key={campo}
//                   control={form.control}
//                   name={
//                     `cliente.${campo}` as
//                       | "cliente.colorPrimarioMarca"
//                       | "cliente.colorSecundarioMarca"
//                       | "cliente.colorTerciarioMarca"
//                       | "cliente.colorAcentoMarca"
//                   }
//                   render={({ field }) => {
//                     return (
//                       <FormItem>
//                         <FormLabel>
//                           {campo
//                             .replace("Marca", "")
//                             .replace("color", "Color ")}
//                         </FormLabel>
//                         <FormControl>
//                           <ColorPicker {...field} value={field.value ?? ""} />
//                         </FormControl>
//                         <FormMessage />
//                       </FormItem>
//                     );
//                   }}
//                 />
//               ))}

//               <FormField
//                 control={form.control}
//                 name="cliente.tipografiaMarca"
//                 render={({ field }) => (
//                   <FormItem className="md:col-span-2">
//                     <FormLabel>Tipografía</FormLabel>
//                     <FormControl>
//                       <SelectTipografia
//                         value={field.value ?? ""}
//                         onChange={(value) => field.onChange(value)}
//                       />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//             </div>

//             <Button type="submit" disabled={isPending} className="w-full">
//               {isPending ? "Guardando cambios..." : "Guardar cambios"}
//             </Button>
//           </form>
//         </Form>
//       </div>
//       <div className="w-full rounded-md overflow-hidden shadow-lg">
//         <div
//           className="h-2 w-full"
//           style={{
//             background: `linear-gradient(to right, ${
//               form.watch("cliente.colorPrimarioMarca") || "#defaultColor"
//             }, ${
//               form.watch("cliente.colorSecundarioMarca") || "#defaultColor"
//             })`,
//           }}
//         />

//         <div className="flex items-center justify-between p-4 bg-[var(--background)]">
//           {/* Izquierda: logo preview */}
//           <div className="flex items-center justify-center w-24 h-16 bg-white rounded-md border mr-4">
//             {logoFile ? (
//               <img
//                 src={URL.createObjectURL(logoFile)}
//                 alt="Logo preview"
//                 className="w-full h-full object-contain rounded-md"
//               />
//             ) : (
//               <img
//                 src={
//                   typeof cliente.logoMarca === "string"
//                     ? cliente.logoMarca
//                     : cliente.logoMarca?.url || defaultLogo
//                 }
//                 alt="Logo Cliente"
//                 className="w-full h-full object-contain rounded-md"
//               />
//             )}
//           </div>

//           {/* Centro: texto con tipografía seleccionada */}
//           <div
//             className="text-center flex-1 px-4"
//             style={{ fontFamily: `var(--font-${selectedFont || "sans"})` }}
//           >
//             <h2 className="text-2xl font-bold">
//               Vista previa de la tipografía
//             </h2>
//             <p className="mt-2">
//               Esta es una vista previa de cómo se verá la tipografía
//               seleccionada en el cliente final.
//             </p>
//           </div>

//           {/* Derecha: logo del cliente final */}
//           <Button
//             size={"lg"}
//             variant="destructive"
//             className="text-black"
//             style={{
//               backgroundColor:
//                 (form.watch("cliente.colorTerciarioMarca") ||
//                   "#defaultColor") ??
//                 undefined,
//               color: form.watch("cliente.colorAcentoMarca") || "#000",
//             }}
//           >
//             boton
//           </Button>
//         </div>
//       </div>
//     </>
//   );
// }
